import {
  Button,
  FormLabel,
  SkeletonText,
  Loading,
  Grid,
  Column,
} from '@carbon/react';
import {
  PaintBrush,
  Globe,
  Sun,
  Moon,
  Checkmark,
  IbmCloudLogging,
} from '@carbon/icons-react';
import { usePreferences, useSetPreference } from '@sphere/user/data-access';
import { toast } from 'sonner';
import { useThemeStore } from '@sphere/shared/data-access/store';
import {
  ErrorView,
  FormSelect,
  FormToggle,
} from '@sphere/shared/ui/design-system';
import { SettingsSection } from './components';
import shared from './settings-pages.shared.module.scss';
import styles from './PreferencesPage.module.scss';

const THEMES = [
  { value: 'LIGHT', label: 'Clair', icon: Sun },
  { value: 'DARK', label: 'Sombre', icon: Moon },
  { value: 'SYSTEM', label: 'Système', icon: IbmCloudLogging },
];

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
];

const TIMEZONES = [
  { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
  { value: 'Europe/London', label: 'Londres (UTC+0)' },
  { value: 'America/New_York', label: 'New York (UTC-5)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
];

export default function PreferencesPage() {
  const { data: preferences, isLoading, error, refetch } = usePreferences();
  const setPreference = useSetPreference();
  const setTheme = useThemeStore((state) => state.setTheme);

  const getPreferenceValue = (key: string, defaultValue = '') => {
    if (!Array.isArray(preferences)) return defaultValue;
    const pref = preferences.find((p) => p.key === key);
    return (pref?.value as string) || defaultValue;
  };

  const handlePreferenceChange = async (
    key: string,
    value: string | boolean
  ) => {
    try {
      await setPreference.mutateAsync({ key, value });

      if (key === 'theme') {
        const themeValue = String(value);
        if (themeValue === 'DARK') setTheme('dark');
        else if (themeValue === 'LIGHT') setTheme('light');
        else if (themeValue === 'SYSTEM') {
          const isDark = window.matchMedia(
            '(prefers-color-scheme: dark)'
          ).matches;
          setTheme(isDark ? 'dark' : 'light');
        }
      }

      toast.success('Préférence mise à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (isLoading) {
    return <PreferencesSkeleton />;
  }

  if (error) {
    return (
      <div className={shared.page}>
        <div className={shared.statePanel}>
          <ErrorView
            title="Erreur de chargement"
            message="Impossible de charger vos préférences."
            error={error as Error}
            onRetry={() => refetch()}
            variant="card"
          />
        </div>
      </div>
    );
  }

  const currentTheme = getPreferenceValue('theme', 'LIGHT');
  const currentLanguage = getPreferenceValue('language', 'fr');
  const currentTimezone = getPreferenceValue('timezone', 'Europe/Paris');
  const sidebarCollapsed =
    getPreferenceValue('sidebar_collapsed', 'false') === 'true';

  return (
    <div className={shared.page}>
      <SettingsSection
        title="Apparence"
        description="Personnalisez l'apparence de l'interface"
        icon={PaintBrush}
      >
        <div className={styles.formGroup}>
          <FormLabel className={styles.formGroupLabel}>Thème</FormLabel>
          <div
            className={styles.themeRow}
            role="group"
            aria-label="Choix du thème"
          >
            {THEMES.map((theme) => {
              const Icon = theme.icon;
              const isSelected = currentTheme === theme.value;

              return (
                <Button
                  key={theme.value}
                  kind={isSelected ? 'primary' : 'tertiary'}
                  className={styles.themeButton}
                  onClick={() => handlePreferenceChange('theme', theme.value)}
                  disabled={setPreference.isPending}
                >
                  {isSelected && (
                    <Checkmark size={16} className={styles.themeCheck} />
                  )}
                  <Icon size={20} />
                  <span>{theme.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <FormToggle
          className={styles.sidebarToggleField}
          id="sidebar-collapsed-toggle"
          label="Barre latérale compacte"
          hint="Réduire la barre latérale par défaut"
          toggled={sidebarCollapsed}
          onToggle={(checked) =>
            handlePreferenceChange('sidebar_collapsed', String(checked))
          }
          disabled={setPreference.isPending}
        />
      </SettingsSection>

      <SettingsSection
        title="Langue et région"
        description="Choisissez la langue d’affichage et le fuseau utilisé pour les heures."
        icon={Globe}
      >
        <fieldset className={styles.localeFieldset}>
          <legend className={styles.localeLegend}>
            Langue et fuseau horaire
          </legend>
          <Grid className={styles.localeGrid}>
            <Column sm={4} md={4} lg={8}>
              <div className={styles.localeField}>
                <FormSelect
                  id="language-select"
                  name="language"
                  label="Langue"
                  hint="Textes de l’interface et formats régionaux"
                  required
                  value={currentLanguage}
                  options={LANGUAGES}
                  onValueChange={(v) => {
                    if (v) handlePreferenceChange('language', v);
                  }}
                  disabled={setPreference.isPending}
                  className={styles.localeSelect}
                />
              </div>
            </Column>
            <Column sm={4} md={4} lg={8}>
              <div className={styles.localeField}>
                <FormSelect
                  id="timezone-select"
                  name="timezone"
                  label="Fuseau horaire"
                  hint="Heures des activités, rappels et contenus datés"
                  required
                  value={currentTimezone}
                  options={TIMEZONES}
                  onValueChange={(v) => {
                    if (v) handlePreferenceChange('timezone', v);
                  }}
                  disabled={setPreference.isPending}
                  className={styles.localeSelect}
                />
              </div>
            </Column>
          </Grid>
        </fieldset>
      </SettingsSection>

      {setPreference.isPending && (
        <div className={shared.savingIndicator}>
          <Loading small withOverlay={false} />
          Enregistrement...
        </div>
      )}
    </div>
  );
}

function PreferencesSkeleton() {
  return (
    <div className={shared.page}>
      <SettingsSection title=" " description=" ">
        <SkeletonText width="8rem" heading />
        <div className={styles.themeRow}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="cds--skeleton cds--skeleton--text"
              style={{
                height: '4.25rem',
                flex: '1 1 5.5rem',
                minWidth: '5.5rem',
                maxWidth: '8rem',
              }}
            />
          ))}
        </div>
        <div className={styles.toggleSkeleton}>
          <SkeletonText width="14rem" />
          <SkeletonText width="22rem" />
          <div
            className="cds--skeleton cds--skeleton--text"
            style={{ height: '2rem', width: '100%', maxWidth: '18rem', marginTop: '0.5rem' }}
          />
        </div>
      </SettingsSection>
      <SettingsSection title=" " description=" ">
        <Grid className={styles.localeGrid}>
          <Column sm={4} md={4} lg={8}>
            <SkeletonText width="5rem" />
            <SkeletonText width="100%" />
            <div
              className="cds--skeleton cds--skeleton--text"
              style={{ height: '2.5rem', marginTop: '0.5rem' }}
            />
          </Column>
          <Column sm={4} md={4} lg={8}>
            <SkeletonText width="7rem" />
            <SkeletonText width="100%" />
            <div
              className="cds--skeleton cds--skeleton--text"
              style={{ height: '2.5rem', marginTop: '0.5rem' }}
            />
          </Column>
        </Grid>
      </SettingsSection>
    </div>
  );
}
